import { lazy, Suspense, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import Condition, { ConditionVariant } from "@components/Condition";
import { OrderSummary } from "@components/layouts/StartConsultationPage";
import useDataLayer from "@hooks/useDataLayer";
import { Title } from "@runwayhealth/runway-components-react";
import { removeConditions, setConditions } from "@store/cases/casesSlice";
import { REQUEST_CASE_PRICE } from "@store/prices/pricesActions";
import { GA4Events } from "@types-runway/analytics";
import { Case } from "@types-runway/case";
import { Condition as ConditionType, ConditionTypes } from "@types-runway/condition";
import { WarningMessage } from "@types-runway/questionnaire";
import { PricesState, RootEntity, RootState } from "@types-runway/state";
import { VISIT_FEE_STRIPE_PRICE_ID } from "@utils/constantsEnv";

// Lazy components
const ConditionsPicker = lazy(() => import("@components/Cart/components/ConditionsPicker"));

interface CartProps {
  country: string;
  variant?: ConditionVariant;
  preselectedConditions?: ConditionType[];
  addonConditions?: ConditionType[];
  setAddonConditions?: React.Dispatch<React.SetStateAction<ConditionType[]>>;
  initialIsSkipped?: boolean;
  onSkipChange?: (isSkipped: boolean) => void;
  visitFeeDiscount?: number;
  cartItemPresentationStyle?: "card" | "selector";
  shouldDisplaySummary?: boolean;
  warningMessage?: WarningMessage;
}

const Cart = ({
  country,
  variant = "consultation",
  preselectedConditions,
  addonConditions,
  setAddonConditions,
  initialIsSkipped,
  onSkipChange,
  visitFeeDiscount,
  cartItemPresentationStyle,
  shouldDisplaySummary = true,
  warningMessage,
}: CartProps) => {
  const dispatch = useDispatch();
  const newCase = useSelector<RootState, RootEntity<Case>>((state) => state.case.newCase);
  const prices = useSelector<RootState, PricesState>((state) => state.price);

  const consultationPrice = useMemo(() => {
    return prices.case.data.unit_amount;
  }, [prices]);

  // Handle Analytic Events
  const { reportAddToCart } = useDataLayer(GA4Events.ADD_TO_CART);
  const { reportRemoveFromCart } = useDataLayer(GA4Events.REMOVE_FROM_CART);

  // Reference for previous conditions - to track additions or removals of the analyitcs events above
  const prevConditionsRef = useRef<ConditionType[]>(newCase.data.conditions);

  useEffect(() => {
    // Set country state if any is selected.
    if (country) sessionStorage.setItem("country", country);
    // Get services prices.
    dispatch({
      type: REQUEST_CASE_PRICE,
      priceId: VISIT_FEE_STRIPE_PRICE_ID,
    });
  }, []);

  // useEffect to track additions or removals for analytics events
  useEffect(() => {
    // Capture the previous and current conditions
    const prevConditions = prevConditionsRef.current;
    const currentConditions = newCase.data.conditions;

    // Add to Cart event
    if (currentConditions.length > prevConditions.length) {
      const addedConditions = currentConditions.filter(
        (condition) => !prevConditions.includes(condition)
      );

      if (addedConditions.length > 0) {
        reportAddToCart(addedConditions[0]);
      }
    }

    // Remove from cart event
    if (currentConditions.length < prevConditions.length) {
      const removedConditions = prevConditions.filter(
        (condition) => !currentConditions.includes(condition)
      );

      if (removedConditions.length > 0) {
        reportRemoveFromCart(removedConditions[0]);
      }
    }

    prevConditionsRef.current = currentConditions;
  }, [newCase.data.conditions]);

  const removeCondition = (condition: string) => {
    dispatch(removeConditions(condition));
  };

  return (
    <>
      {shouldDisplaySummary && (
        <OrderSummary $startPage={variant === "consultation"}>
          {variant === "consultation" && (
            <Title size="xs" as="h3" className="order-items-title">
              Your Order
            </Title>
          )}
          {variant === "upsell" && <Title className="upsell-title">Current Selection</Title>}
          {!newCase.isPending && !prices.prescriptions.isPending ? (
            <div className="order-items-container">
              {/* Set Runway Plus as Default Condition */}
              <Condition
                key={"condition-0"}
                condition={{
                  product: {
                    id: VISIT_FEE_STRIPE_PRICE_ID ?? "",
                    name: ConditionTypes.RUNWAY_PLUS,
                    units: 1,
                    metadata: {
                      name: "Visit Fee",
                    },
                  },
                  price: consultationPrice,
                  suggestedMedication: {
                    quantity: 1,
                  },
                }}
                discount={visitFeeDiscount}
              />
              {newCase.data.conditions.map((condition) => (
                <Condition
                  key={condition.product.id}
                  removeCondition={removeCondition}
                  condition={condition}
                  addonConditions={addonConditions}
                  variant={variant}
                />
              ))}
            </div>
          ) : (
            <>...</>
          )}
        </OrderSummary>
      )}
      {variant !== "consultation" && (
        <Suspense fallback={<>...</>}>
          <ConditionsPicker
            variant={variant}
            upsellConditions={preselectedConditions}
            addonConditions={addonConditions}
            setAddonConditions={setAddonConditions}
            initialIsSkipped={initialIsSkipped}
            onSkipChange={onSkipChange}
            cartItemPresentationStyle={cartItemPresentationStyle}
            warningMessage={warningMessage}
          />
        </Suspense>
      )}
    </>
  );
};

export default Cart;
